import React, { useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { motion } from 'framer-motion';
import { Check, Users, Building, User } from 'lucide-react';
export function SelfDefenseSeminarsPage() {
  useEffect(() => {
    document.title = "Self Defense Seminars | Walker's Martial Arts | Bloomington, Illinois";
  }, []);
  return <div className="bg-black min-h-screen" data-id="element-985">
      <Navigation data-id="element-986" />

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 overflow-hidden" data-id="element-987">
        <div className="absolute inset-0 z-0" data-id="element-988">
          <img src="https://images.squarespace-cdn.com/content/v1/6782c238cf9313348308674a/1736957703336-TKRC2PNOIT6O3HJH366K/self+defense+blank.png" alt="Self Defense Seminars" className="w-full h-full object-contain opacity-20" data-id="element-989" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" data-id="element-990" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-991">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-992">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-993">
              <span className="text-[#00D4FF]" data-id="element-994">Self Defense</span> Seminars
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-995">
              Specialized self-defense training for individuals, groups, and
              corporate teams. Learn practical skills to stay safe and
              confident.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Seminar Types */}
      <section className="py-20 bg-[#0a0a0a]" data-id="element-996">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-997">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-998">
            Seminar Options
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-999">
            {[{
            icon: User,
            title: 'Individual Sessions',
            description: 'One-on-one training focused on your specific concerns and personal safety needs.',
            features: ['Personalized instruction', 'Flexible scheduling', 'Customized curriculum', 'Private, comfortable environment']
          }, {
            icon: Users,
            title: 'Group Workshops',
            description: 'Interactive seminars for schools, community groups, and organizations.',
            features: ['Engaging group activities', 'Practical scenarios', 'Team building', 'Scalable for any group size']
          }, {
            icon: Building,
            title: 'Corporate Training',
            description: 'Professional self-defense training for workplace safety and employee wellness.',
            features: ['On-site or in-studio', 'Workplace safety focus', 'Team building component', 'Flexible duration options']
          }].map((type, index) => <motion.div key={type.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-[#1a1a1a] p-8 rounded-2xl border border-gray-800" data-id="element-1000">
                <div className="w-16 h-16 bg-[#00D4FF]/10 rounded-full flex items-center justify-center mb-6" data-id="element-1001">
                  <type.icon className="w-8 h-8 text-[#00D4FF]" data-id="element-1002" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1003">
                  {type.title}
                </h3>
                <p className="text-gray-400 mb-6" data-id="element-1004">{type.description}</p>
                <ul className="space-y-2" data-id="element-1005">
                  {type.features.map(feature => <li key={feature} className="flex items-start gap-2 text-sm text-gray-300" data-id="element-1006">
                      <Check className="w-4 h-4 text-[#00D4FF] shrink-0 mt-0.5" data-id="element-1007" />
                      {feature}
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-black" data-id="element-1008">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1009">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1010">
            <div data-id="element-1011">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1012">
                What You'll Learn
              </h2>
              <div className="space-y-4" data-id="element-1013">
                {['Situational awareness and threat assessment', 'Verbal de-escalation techniques', 'Physical self-defense fundamentals', 'Escape and evasion strategies', 'Defense against common attacks', 'Building confidence and mental preparedness'].map(item => <div key={item} className="flex items-start gap-3" data-id="element-1014">
                    <Check className="w-5 h-5 text-[#00D4FF] shrink-0 mt-1" data-id="element-1015" />
                    <p className="text-gray-300" data-id="element-1016">{item}</p>
                  </div>)}
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden" data-id="element-1017">
              <img src="https://images.squarespace-cdn.com/content/v1/6782c238cf9313348308674a/1736957703336-TKRC2PNOIT6O3HJH366K/self+defense+blank.png" alt="Self Defense Training" className="w-full h-full object-contain" data-id="element-1018" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-1019" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0a0a0a]" data-id="element-1020">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1021">
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1022">
            Ready to Schedule Your Seminar?
          </h2>
          <p className="text-xl text-gray-300 mb-8" data-id="element-1023">
            Contact us to discuss your specific needs and customize a
            self-defense seminar for your group or organization.
          </p>
          <a href="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-bold rounded-full hover:bg-[#33ddff] transition-colors shadow-[0_0_20px_rgba(0,212,255,0.4)]" data-id="element-1024">
            Contact Us
          </a>
        </div>
      </section>

      <Footer data-id="element-1025" />
    </div>;
}